import { PlanningPeriod } from '@digiwall/goliath-documents/proxy';

/** One kanban column: a single granularity unit (day, week or month). */
export interface PlanningScaleBucket {
  /** ISO yyyy-MM-dd of the bucket start; stable column id. */
  id: string;
  start: Date;
  /** Exclusive end (start of the next bucket). */
  end: Date;
}

export function toIsoDay(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  const result = startOfDay(date);
  result.setDate(result.getDate() + days);
  return result;
}

function startOfWeek(date: Date): Date {
  const result = startOfDay(date);
  const mondayOffset = (result.getDay() + 6) % 7; // Monday = 0
  return addDays(result, -mondayOffset);
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function startOfQuarter(date: Date): Date {
  const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
  return new Date(date.getFullYear(), quarterStartMonth, 1);
}

function startOfSemester(date: Date): Date {
  const semesterStartMonth = Math.floor(date.getMonth() / 6) * 6;
  return new Date(date.getFullYear(), semesterStartMonth, 1);
}

function startOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1);
}

/** The visible window (span) for a given zoom range around an anchor date. */
export function planningWindow(range: PlanningPeriod, anchor: Date): { start: Date; end: Date } {
  switch (range) {
    case PlanningPeriod.Week: {
      const start = startOfWeek(anchor);
      return { start, end: addDays(start, 7) };
    }
    case PlanningPeriod.Quarter: {
      const start = startOfQuarter(anchor);
      return { start, end: addMonths(start, 3) };
    }
    case PlanningPeriod.Semester: {
      const start = startOfSemester(anchor);
      return { start, end: addMonths(start, 6) };
    }
    case PlanningPeriod.Year: {
      const start = startOfYear(anchor);
      return { start, end: addMonths(start, 12) };
    }
    default: {
      // Day / Month → the calendar month.
      const start = startOfMonth(anchor);
      return { start, end: addMonths(start, 1) };
    }
  }
}

/** Splits the visible window into columns of one granularity unit each. */
export function buildPlanningBuckets(
  granularity: PlanningPeriod,
  range: PlanningPeriod,
  anchor: Date,
): PlanningScaleBucket[] {
  const window = planningWindow(range, anchor);
  const buckets: PlanningScaleBucket[] = [];

  const step =
    granularity === PlanningPeriod.Week
      ? (date: Date) => addDays(date, 7)
      : granularity === PlanningPeriod.Month
        ? (date: Date) => addMonths(date, 1)
        : (date: Date) => addDays(date, 1);

  let cursor =
    granularity === PlanningPeriod.Week
      ? startOfWeek(window.start)
      : granularity === PlanningPeriod.Month
        ? startOfMonth(window.start)
        : startOfDay(window.start);

  // Guard against a misconfigured (non-coarser) range producing a runaway loop.
  let safety = 400;
  while (cursor < window.end && safety-- > 0) {
    const next = step(cursor);
    buckets.push({ id: toIsoDay(cursor), start: cursor, end: next });
    cursor = next;
  }
  return buckets;
}

/**
 * True when the bucket is a weekend (Saturday/Sunday) day column. Only meaningful at Day granularity —
 * week/month buckets span whole weeks/months and are never treated as weekends.
 */
export function isWeekendBucket(granularity: PlanningPeriod, bucket: PlanningScaleBucket): boolean {
  if (granularity !== PlanningPeriod.Day) {
    return false;
  }
  const day = bucket.start.getDay();
  return day === 0 || day === 6;
}

/** Moves the anchor one window (one range unit) backward or forward. */
export function shiftAnchor(range: PlanningPeriod, anchor: Date, direction: -1 | 1): Date {
  switch (range) {
    case PlanningPeriod.Week:
      return addDays(startOfWeek(anchor), direction * 7);
    case PlanningPeriod.Quarter:
      return addMonths(startOfQuarter(anchor), direction * 3);
    case PlanningPeriod.Semester:
      return addMonths(startOfSemester(anchor), direction * 6);
    case PlanningPeriod.Year:
      return addMonths(startOfYear(anchor), direction * 12);
    default:
      return addMonths(startOfMonth(anchor), direction);
  }
}
